/**
 * Shared API client for backend calls.
 * All requests include credentials (HttpOnly JWT cookie).
 */

const BASE_URL = process.env.VUE_APP_API_URL || 'http://localhost:8080'

class ApiError extends Error {
  constructor(status, message, data) {
    super(message)
    this.status = status
    this.data = data
  }
}

function buildUrl(path, params) {
  let url = `${BASE_URL}${path}`
  if (params) {
    const searchParams = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value)
      }
    }
    const qs = searchParams.toString()
    if (qs) url += `?${qs}`
  }
  return url
}

async function request(method, path, { body, params } = {}) {
  const url = buildUrl(path, params)

  const options = {
    method,
    credentials: 'include',
    headers: {}
  }

  if (body !== undefined) {
    options.headers['Content-Type'] = 'application/json'
    options.body = JSON.stringify(body)
  }

  const response = await fetch(url, options)

  if (response.status === 401) {
    // Session expired — redirect to login
    window.location.href = '/login?expired=true'
    throw new ApiError(401, 'Unauthorized')
  }

  if (!response.ok) {
    const data = await response.json().catch(() => null)
    throw new ApiError(response.status, data?.message || response.statusText, data)
  }

  // Handle 204 No Content
  if (response.status === 204) return null

  return response.json()
}

export const api = {
  get: (path, params) => request('GET', path, { params }),
  post: (path, body) => request('POST', path, { body }),
  put: (path, body) => request('PUT', path, { body }),
  patch: (path, body) => request('PATCH', path, { body }),
  delete: (path) => request('DELETE', path),

  /**
   * Download a file as a blob. Triggers browser save dialog.
   */
  download: async (path, fallbackFilename = 'download', params = null) => {
    const response = await fetch(buildUrl(path, params), {
      method: 'GET',
      credentials: 'include'
    })

    if (response.status === 401) {
      window.location.href = '/login?expired=true'
      throw new ApiError(401, 'Unauthorized')
    }

    if (!response.ok) {
      throw new ApiError(response.status, response.statusText)
    }

    const blob = await response.blob()
    const contentDisposition = response.headers.get('Content-Disposition')
    const filename = contentDisposition?.match(/filename="?(.+?)"?$/)?.[1] || fallbackFilename

    const objectUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(objectUrl)
  },

  /**
   * Upload a file via multipart/form-data.
   */
  upload: async (path, formData) => {
    const response = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      credentials: 'include',
      body: formData // browser sets Content-Type with boundary
    })

    if (!response.ok) {
      const data = await response.json().catch(() => null)
      throw new ApiError(response.status, data?.message || response.statusText, data)
    }

    return response.json()
  }
}

export { ApiError }
